import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import { fetchLocationData, creatProject } from "../Services/APIService";
import { useLocation } from "react-router-dom";
import OverlayLoader from "../Components/OverlayLoader";
import Select from "react-select";

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: any) => void;
  onCreateSuccess?: (data: any) => void;
}

interface ValidationErrors {
  projectName?: string;
  description?: string;
  locationID?: string;
}

interface Location {
  locationID: number;
  locationName: string;
  isActive: boolean;
}

interface locationOption {
  value: number;
  label: string;
}

interface Project {
  projectID: number;
  projectName: string;
  description: string;
  locationID: number;
  contactMails: string;
  brochure: string;
  priceQuote: string;
  tenantId: number;
  isActive: boolean;
  locationName: string;
  isAssignTabShown?: boolean;
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onCreateSuccess,
}) => {
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<locationOption[]>(
    []
  );
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    projectName: "",
    description: "",
    locationID: "",
    isActive: true,
  });

  useEffect(() => {
    if (isOpen) {
      const fetchLocations = async () => {
        setIsLoadingLocations(true);
        try {
          const response = await fetchLocationData();
          if (!response) {
            throw new Error("Failed to fetch location data");
          }
          const activeLocations = response.filter(
            (loc: Location) => loc.isActive === true
          );
          setLocations(activeLocations);
          const locationOptions = activeLocations.map((location) => ({
            value: location.locationID,
            label: location.locationName,
          }));
          setSelectedLocation(locationOptions);
        } catch (error) {
          console.error("Error fetching location data:", error);
          toast.error("Failed to load locations");
        } finally {
          setIsLoadingLocations(false);
        }
      };
      fetchLocations();
    }
  }, [isOpen]);

  useEffect(() => {
    const getMenus = async () => {
      try {
        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching menus:", error);
      }
    };

    if (isOpen) {
      getMenus();
    }
  }, [isOpen, location.state]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;

    // Handle checkboxes
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({
        ...formData,
        [name]: checked,
      });
      return;
    }

    // Prevent leading spaces for all inputs
    if (value.startsWith(" ")) {
      return;
    }

    // Update form data
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear errors when user types
    if (errors[name as keyof ValidationErrors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    // Required field validations
    if (!formData.projectName.trim()) {
      newErrors.projectName = "Project name is required";
      isValid = false;
    }

    if (!formData.locationID) {
      newErrors.locationID = "Location is required";
      isValid = false;
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    // Validate form before submission
    if (!validateForm()) {
      //toast.error("Please fix the errors in the form");
      return;
    }

    const userId = localStorage.getItem("UserId");
    const tenantId = localStorage.getItem("tenantId");

    const body = {
      projectName: formData.projectName,
      locationID: parseInt(formData.locationID),
      description: formData.description,
      tenantId: tenantId ? parseInt(tenantId) : 0,
      isActive: formData.isActive,
      createdBy: userId ? parseInt(userId) : 0,
      brochure: "",
      priceQuote: "",
    };

    const result = await creatProject(body);
    console.log("Create Project Result:", result);

    if (result.isCreated) {
      const mappedData: Project = {
        projectID: result.data.projecT_ID,
        projectName: result.data.projecT_NAME,
        description: result.data.description,
        locationID: result.data.locatioN_ID,
        contactMails: "",
        brochure: "",
        priceQuote: "",
        tenantId: result.data.tenanT_ID,
        isActive: result.data.iS_ACTIVE,
        locationName: "",
      };
      toast.success(result.message);
      if (onCreateSuccess) {
        onCreateSuccess(mappedData);
      }
      if (onSubmit) {
        onSubmit(formData);
      }
      handleClose();
      setTimeout(() => {
        navigate("/projectView");
      }, 1000);
    } else {
      toast.error(result.message || "Project creation failed");
    }
  };

  const handleLocationChange = (selectedOption: locationOption | null) => {
    const locationId = selectedOption ? selectedOption.value : 0;
    setFormData({
      ...formData,
      locationID: locationId.toString(),
    });
    setErrors((prev) => ({ ...prev, locationID: undefined }));
  };

  const selectedOption =
    selectedLocation.find(
      (option) => option.value === parseInt(formData.locationID)
    ) || null;

  const handleClose = () => {
    setFormData({
      projectName: "",
      description: "",
      locationID: "",
      isActive: true,
    });
    setErrors({});
    onClose();
  };

  // CSS for required field indicator
  const requiredField = <span className="text-danger ms-1">*</span>;

  return (
    <>
      <div>
        <Toaster position="top-center" reverseOrder={false} />
      </div>

      <div
        className={`modal-backdrop-custom ${isOpen ? "open" : "closed"}`}
        onClick={handleClose}
      ></div>

      <div className={`modal-custom ${isOpen ? "open" : "closed"}`}>
        <div className="modal-content">
          <div className="modal-header-custom">
            <h5 className="modal-title-custom">Add New Project</h5>
            <button
              type="button"
              className="btn-close-custom"
              onClick={handleClose}
            >
              <span className="icon-close"></span>
            </button>
          </div>

          <div className="modal-body-custom">
            <div className="modal-body-content">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="projectName">
                      Project Name{requiredField}
                    </label>
                    <div className="input-with-icon">
                      <span className="input-icon icon-briefcase"></span>
                      <input
                        name="projectName"
                        id="projectName"
                        placeholder="Enter Project Name"
                        value={formData.projectName}
                        onChange={handleChange}
                        required
                        className={`form-control-custom ${
                          errors.projectName ? "is-invalid" : ""
                        }`}
                      />
                      {errors.projectName && (
                        <div className="invalid-feedback-custom">
                          {errors.projectName}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="locationID">
                      Location{requiredField}
                    </label>
                    {/* <select
                      name="locationID"
                      id="locationID"
                      value={formData.locationID || ""}
                      onChange={handleChange}
                      required
                      className={`form-select form-control-lg ${
                        errors.locationID ? "is-invalid" : ""
                      }`}
                      disabled={isLoadingLocations}
                    >
                      {isLoadingLocations ? (
                        <option value="">Loading locations...</option>
                      ) : (
                        <>
                          <option value="">Select Location</option>
                          {locations.map((location) => (
                            <option
                              key={location.locationID}
                              value={location.locationID}
                            >
                              {location.locationName}
                            </option>
                          ))}
                        </>
                      )}
                    </select> */}
                    <Select
                      options={selectedLocation}
                      value={selectedOption}
                      onChange={handleLocationChange}
                      className={`basic-single-select ${
                        errors.locationID ? "is-invalid" : ""
                      }`}
                      classNamePrefix="select"
                      placeholder="Select Location"
                      isSearchable={true}
                      isClearable={true}
                      id="locationID"
                      name="locationID"
                    />
                    {errors.locationID && (
                      <div className="invalid-feedback-custom">
                        {errors.locationID}
                      </div>
                    )}
                  </div>

                  <div className="col-12 col-lg-12 mb-3 input-group-custom">
                    <label className="form-label" htmlFor="description">
                      Description{requiredField}
                    </label>
                    <textarea
                      name="description"
                      placeholder="Enter Description"
                      value={formData.description}
                      onChange={handleChange}
                      required
                      rows={4}
                      className={`form-control-custom-textarea ${
                        errors.description ? "is-invalid" : ""
                      }`}
                    />
                    {errors.description && (
                      <div className="invalid-feedback-custom">
                        {errors.description}
                      </div>
                    )}
                  </div>

                  <div className="col-12 col-lg-12 mb-3 mt-3 d-none">
                    <label className="form-check">
                      Is Active
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleChange}
                        className="form-check-input"
                      />
                    </label>
                  </div>
                </div>
              </form>
            </div>

            <div className="modal-footer-custom">
              <button
                type="button"
                onClick={handleClose}
                className="btn-custom btn-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="btn-custom btn-primary-custom"
              >
                Create Project
              </button>
            </div>
          </div>
        </div>
        <OverlayLoader
          loading={isLoadingLocations}
          message="Loading Data"
          submessage="This might take a moment..."
        />
      </div>
    </>
  );
};

export default CreateProjectModal;
